import { FC } from "react"
import { Badge, Button, Card, Col, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import ExternalContent from "../ExternalContent/ExternalContentComponent"

type BuildingComponentProps = {
    building?: any
}

const BuildingComponent: FC<BuildingComponentProps> = ({ building }) => {
    const { t } = useTranslation("site")

    if (!building) {
        return null
    }

    return (
        <Row className="g-3 align-items-stretch">
            <Col xs={12} lg={6} className="d-flex">
                <Card className="w-100 h-100">
                    <Card.Header>
                        <h4>
                            <i className="bi bi-buildings"></i>{" "}
                            {t("site.building")}
                        </h4>
                    </Card.Header>

                    <Card.Body className="h-100">
                        <Row className="g-4 align-items-stretch h-100">
                            <Col xs={12} md={6}>
                                <div className="position-relative h-100">
                                    <Card.Img
                                        src={
                                            "/static/" +
                                            building.identifier.replace("|", "").toLowerCase() +
                                            ".jpg"
                                        }
                                        alt={building.name}
                                        className="w-100 h-100 rounded"
                                        style={{
                                            objectFit: "cover",
                                        }}
                                    />
                                    {(building.imageAttribution?.sourceUrl ||
                                        building.imageAttribution?.author ||
                                        building.imageAttribution?.license) && (
                                        <div className="position-absolute bottom-0 start-0 end-0 small text-body px-2 py-1 bg-body bg-opacity-75 rounded-bottom">
                                            {t("site.imageCredit")}{" "}
                                            {building.imageAttribution.author ||
                                                t("site.imageCreditUnknown")}
                                            {building.imageAttribution.sourceUrl && <>
                                                {" · "}
                                                <a href={building.imageAttribution.sourceUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer">
                                                    {t("site.imageSource")}
                                                </a>
                                            </>}
                                            {building.imageAttribution.license && <>
                                                {" · "}
                                                {building.imageAttribution.licenseUrl
                                                    ? <a href={building.imageAttribution.licenseUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer">
                                                        {building.imageAttribution.license}
                                                    </a>
                                                    : building.imageAttribution.license}
                                            </>}
                                        </div>
                                    )}

                                    <Badge
                                        bg="danger"
                                        className="position-absolute top-0 start-0 m-3 fs-3"
                                    >
                                        {building.identifier}
                                    </Badge>
                                </div>
                            </Col>

                            <Col xs={12} md={6}>
                                <div className="h-100 d-flex flex-column">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between gap-3 mb-4">
                                        <div>
                                            <h4 className="mb-1">{building.name}</h4>
                                            <div className="text-muted">
                                                {t("site.buildingLocation", {
                                                    campus: building.campus,
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border rounded p-3 mb-3">
                                        <div className="text-muted small mb-1">
                                            <i className="bi bi-sign-turn-slight-right"></i>{" "}
                                            {t("site.address")}
                                        </div>
                                        <div>{building.street}</div>
                                        <div>{building.postcode}</div>
                                    </div>

                                    <div className="border rounded p-3 mb-3">
                                        <div className="text-muted small mb-1">
                                            <i className="bi bi-geo-alt"></i>{" "}
                                            {t("site.coordinates")}
                                        </div>
                                        <div>
                                            {building.latitude}, {building.longitude}
                                        </div>
                                    </div>

                                    <div className="border rounded p-3 mb-3">
                                        <div className="text-muted small mb-2">
                                            <i className="bi bi-train-front"></i>{" "}
                                            {t("site.publicTransport")}
                                        </div>

                                        {building.stopsNearby?.length ? (
                                            <div className="d-flex flex-wrap gap-2">
                                                {building.stopsNearby.map((stop: string) => (
                                                    <Badge
                                                        key={stop}
                                                        bg="secondary"
                                                        pill
                                                        className="fs-6"
                                                    >
                                                        {stop}
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-muted">
                                                {t("site.noPublicTransport")}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-auto d-flex flex-wrap gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            href={building.campusMapUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="bi bi-box-arrow-up-right"></i>{" "}
                                            {t("site.campusInfo")}
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </Col>

            <Col xs={12} lg={6} className="d-flex">
                <Card className="w-100 h-100">
                    <Card.Header>
                        <h4>
                            <i className="bi bi-map"></i>{" "}
                            {t("site.map")}
                        </h4>
                    </Card.Header>

                    <Card.Body className="d-flex flex-column">
                        <ExternalContent
                            title={t("site.openStreetMap")}
                            height={450}
                            src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                                Number(building.longitude) - 0.001
                            }%2C${
                                Number(building.latitude) - 0.001
                            }%2C${
                                Number(building.longitude) + 0.001
                            }%2C${
                                Number(building.latitude) + 0.001
                            }&layer=mapnik&marker=${building.latitude}%2C${building.longitude}`}
                            link={`https://www.openstreetmap.org/?mlat=${building.latitude}&mlon=${building.longitude}#map=19/${building.latitude}/${building.longitude}`}
                            linkText={t("site.showLargerMap")}
                        />
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    )
}

export default BuildingComponent
